import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FolderIcon,
  RocketLaunchIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { ProjectFormElement, WorkflowContainer, User, UserRole } from "../types";
import { ProjectDetailModal } from "../components/projects/ProjectDetailModal";
import { ProjectFilesModal } from "../components/projects/ProjectFilesModal";
import { WorkflowStartModal } from "../components/workflow/WorkflowStartModal";
import { apiService } from "../services/apiService";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { buildProjectCode } from "../utils/projectCode";
import { isProjectManagerAllowedForTemplate } from "../utils/pmWorkflowAccess";
import { useVirtualizedRows } from "./manager-dashboard/hooks/useVirtualizedRows";
import {
  formatCurrencyByCode,
  getCurrencySuffixLabel,
} from "../utils/currency";

interface WorkflowInstanceItem {
  id: number;
  name: string;
  description?: string;
  status: string;
  workflowContainerId: number;
  workflowContainerName: string;
  templateWorkflowId?: number;
  templateWorkflowName?: string;
  projectCode?: string;
  projectManagerName?: string | null;
  projectMembers?: number[];
  googleDriveFolderId?: string;
  totalSpentHours?: number;
  workflowContainerFormData?: {
    startDate?: string;
    endDate?: string;
    clientName?: string;
    projectManager?: number | string;
    project_manager?: number | string;
    [key: string]: any;
  };
  startedAt: string;
  completedAt?: string;
  createdAt: string;
}

type SortField = string; // Changed from union to string to support dynamic listColumn IDs
type SortOrder = "asc" | "desc" | null;

const normalizeSearchText = (value: string): string =>
  value.toLocaleLowerCase("tr-TR");

const FINANCIAL_HIDDEN_TYPES = new Set([
  "budget_input",
  "currency_input",
  "offer_amount",
]);

const FINANCIAL_HIDDEN_FIELD_IDS = new Set([
  "budget",
  "projectbudget",
  "projectcurrency",
  "currency",
  "currencycode",
  "para_birimi",
  "parabirimi",
  "project_currency",
  "offer_amount",
]);

const normalizeFinancialToken = (value: unknown): string =>
  String(value ?? "")
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9_ ]/g, "");

const normalizeFieldToken = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]/g, "");

const isFinancialSensitiveProjectField = (element: ProjectFormElement): boolean => {
  if (FINANCIAL_HIDDEN_TYPES.has(element.type)) {
    return true;
  }
  const idToken = normalizeFinancialToken(element.id);
  if (FINANCIAL_HIDDEN_FIELD_IDS.has(idToken)) {
    return true;
  }
  const labelToken = normalizeFinancialToken(element.label);
  return (
    labelToken.includes("para birimi") ||
    labelToken.includes("doviz") ||
    labelToken.includes("currency")
  );
};

const getDateSearchTokens = (value: unknown): string[] => {
  if (typeof value !== "string") {
    return [];
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  const parsedDate = new Date(trimmed);
  if (Number.isNaN(parsedDate.getTime())) {
    return [];
  }

  const tokens = [
    parsedDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
    parsedDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
    }),
    parsedDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
    }),
    parsedDate.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "short",
    }),
    parsedDate.toLocaleDateString("tr-TR"),
  ];

  return tokens.map((token) => normalizeSearchText(token));
};

const matchesFormDataSearch = (
  formData: unknown,
  searchLower: string,
  extraValues: string[] = []
): boolean => {
  const tokens: string[] = [];

  extraValues.forEach((value) => {
    if (value) {
      tokens.push(normalizeSearchText(value));
    }
  });

  if (!formData) {
    return tokens.some((token) => token.includes(searchLower));
  }

  if (typeof formData === "string") {
    tokens.push(normalizeSearchText(formData));
    try {
      const parsed = JSON.parse(formData);
      if (parsed && typeof parsed === "object") {
        Object.values(parsed).forEach((value) => {
          tokens.push(...getDateSearchTokens(value));
        });
      }
    } catch {
      // ignore
    }
  } else {
    try {
      tokens.push(normalizeSearchText(JSON.stringify(formData)));
    } catch {
      // ignore
    }

    if (typeof formData === "object" && formData) {
      Object.values(formData).forEach((value) => {
        tokens.push(...getDateSearchTokens(value));
      });
    }
  }

  if (!tokens.length) {
    return false;
  }

  const normalizedTokens = tokens.map((token) => token.replace(/_/g, " "));
  return normalizedTokens.some((token) => token.includes(searchLower));
};

const getPlannedHours = (formData: unknown): number => {
  if (!formData) {
    return 0;
  }

  let data: any = formData;
  if (typeof formData === "string") {
    try {
      data = JSON.parse(formData);
    } catch {
      return 0;
    }
  }

  const raw =
    data?.estimatedDurationHours ??
    data?.estimatedDurationDays ??
    data?.duration;
  if (raw === undefined || raw === null) {
    return 0;
  }

  const value = typeof raw === "string" ? Number.parseFloat(raw) : Number(raw);
  return Number.isFinite(value) ? value : 0;
};

const formatHoursTr = (value: number): string =>
  Number.isFinite(value) ? value.toFixed(2).replace(".", ",") : "-";

const getProjectHourMetrics = (instance: WorkflowInstanceItem) => {
  const spentHours = Number(instance.totalSpentHours ?? 0);
  const plannedHours = getPlannedHours(instance.workflowContainerFormData);
  const hasPlannedHours = Number.isFinite(plannedHours) && plannedHours > 0;

  return {
    spentHours,
    plannedHours: hasPlannedHours ? plannedHours : null,
    remainingHoursRaw: hasPlannedHours ? plannedHours - spentHours : null,
  };
};

export const ProjectInstances: React.FC = () => {
  const { t } = useTranslation([
    "workflows",
    "projects",
    "common",
    "dashboard",
    "errors",
    "tasks",
  ]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getWorkflowLabel, isAdmin, isManager, user } = useAuth();
  const isVisitorUser = user?.role === UserRole.VISITOR;

  // Get workflowId from URL query parameter
  const workflowIdFromUrl = searchParams.get("workflowId");
  const isWorkflowScoped = Boolean(workflowIdFromUrl);
  const scopedWorkflowId = useMemo(() => {
    if (!workflowIdFromUrl) return null;
    const parsed = Number.parseInt(workflowIdFromUrl, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [workflowIdFromUrl]);

  // Company-specific terminology
  const workflowText = getWorkflowLabel();
  const canViewFinancials = isAdmin() || isManager();
  const canVisitorViewFinancialDetails = useMemo(() => {
    if (user?.role !== UserRole.VISITOR || !user.settingsJson) {
      return false;
    }
    try {
      const settings = JSON.parse(user.settingsJson);
      return Boolean(settings?.workflowPermissions?.visitorFinancialAccess);
    } catch {
      return false;
    }
  }, [user?.role, user?.settingsJson]);
  const canViewProjectFiles = !isVisitorUser || canVisitorViewFinancialDetails;
  const workflowTextLower = workflowText.toLocaleLowerCase("tr-TR");

  // Data state
  const [instances, setInstances] = useState<WorkflowInstanceItem[]>([]);
  const [employees, setEmployees] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [workflowFormElements, setWorkflowFormElements] = useState<
    ProjectFormElement[]
  >([]);
  const [workflowTemplateName, setWorkflowTemplateName] = useState<
    string | null
  >(null);

  // Get workflow name from instances if workflowId is in URL
  const workflowName = useMemo(() => {
    if (!workflowIdFromUrl || instances.length === 0) return null;
    const workflowId = parseInt(workflowIdFromUrl, 10);
    const instance = instances.find(
      (inst) => inst.templateWorkflowId === workflowId
    );
    return instance?.templateWorkflowName || null;
  }, [workflowIdFromUrl, instances]);
  const workflowTypeLabel =
    workflowName || workflowTemplateName || workflowText;

  // Modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProject, setSelectedProject] =
    useState<WorkflowContainer | null>(null);
  const [selectedInstance, setSelectedInstance] =
    useState<WorkflowInstanceItem | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInstanceForEdit, setSelectedInstanceForEdit] =
    useState<WorkflowInstanceItem | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [scopedWorkflowContainerId, setScopedWorkflowContainerId] = useState<
    number | null
  >(null);

  // Files modal state
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [selectedProjectForFiles, setSelectedProjectForFiles] =
    useState<WorkflowInstanceItem | null>(null);

  // Table state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [workflowFilter, setWorkflowFilter] = useState<string>("all");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [managerFilter, setManagerFilter] = useState<string>("all");
  const [startDateFilter, setStartDateFilter] = useState<string>("");
  const [endDateFilter, setEndDateFilter] = useState<string>("");
  const [sortField, setSortField] = useState<SortField>("startedAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );
  const canLoadCompanyUsers =
    isAdmin() || isManager() || user?.role === UserRole.PROJECT_MANAGER;
  const employeeById = useMemo(
    () => new Map(employees.map((employee) => [employee.id, employee])),
    [employees]
  );
  const getManagerNameForInstance = useCallback(
    (instance: WorkflowInstanceItem): string => {
      const directName =
        typeof instance.projectManagerName === "string"
          ? instance.projectManagerName.trim()
          : "";
      if (directName) {
        return directName;
      }
      const managerId =
        instance.workflowContainerFormData?.projectManager ??
        instance.workflowContainerFormData?.project_manager;
      if (!managerId) {
        return "";
      }
      const manager = employeeById.get(Number(managerId));
      return manager ? `${manager.firstName} ${manager.lastName}`.trim() : "";
    },
    [employeeById]
  );
  const canCreateScopedProject =
    Boolean(scopedWorkflowId) &&
    !isVisitorUser &&
    (isAdmin() ||
      isManager() ||
      isProjectManagerAllowedForTemplate(user, scopedWorkflowId));
  const resolvedScopedProjectId = useMemo(() => {
    if (
      typeof scopedWorkflowContainerId === "number" &&
      scopedWorkflowContainerId > 0
    ) {
      return scopedWorkflowContainerId;
    }

    if (!scopedWorkflowId) {
      return null;
    }

    const instanceWithProject = instances.find(
      (instance) =>
        instance.templateWorkflowId === scopedWorkflowId &&
        typeof instance.workflowContainerId === "number" &&
        instance.workflowContainerId > 0
    );

    return instanceWithProject?.workflowContainerId ?? null;
  }, [instances, scopedWorkflowContainerId, scopedWorkflowId]);

  useEffect(() => {
    loadInstances();
    if (canLoadCompanyUsers) {
      loadEmployees();
    } else {
      setEmployees([]);
    }
  }, [canLoadCompanyUsers]);

  useEffect(() => {
    if (!workflowIdFromUrl) {
      setWorkflowFormElements([]);
      setWorkflowTemplateName(null);
      setScopedWorkflowContainerId(null);
      setColumnFilters({});
      return;
    }

    const loadWorkflowMeta = async () => {
      try {
        const workflowId = parseInt(workflowIdFromUrl, 10);
        const workflow = await apiService.getWorkflowById(workflowId);
        setWorkflowTemplateName(workflow.name || null);
        const containerId =
          typeof workflow.workflowContainerId === "number" &&
          workflow.workflowContainerId > 0
            ? workflow.workflowContainerId
            : null;
        setScopedWorkflowContainerId(containerId);

        if (containerId) {
          const container = await apiService.getWorkflowContainerById(
            containerId
          );
          setWorkflowFormElements(container.formElements || []);
        } else {
          setWorkflowFormElements([]);
        }
      } catch (error) {
        setWorkflowFormElements([]);
        setScopedWorkflowContainerId(null);
      }
    };

    loadWorkflowMeta();
  }, [workflowIdFromUrl]);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const data = await apiService.getWorkflowInstances();
      setInstances(data);
    } catch (error) {
      toast.error(t("projects:projectsLoadError"));
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    if (!canLoadCompanyUsers) {
      return;
    }
    try {
      const users = await apiService.getUsers(true);
      setEmployees(users);
    } catch (error) {
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getProjectDisplayName = (instance: WorkflowInstanceItem): string => {
    const name =
      instance.workflowContainerName ||
      instance.workflowContainerFormData?.name ||
      instance.name;
    return name || "-";
  };

  const getProjectCode = (instance: WorkflowInstanceItem): string => {
    if (instance.projectCode) {
      return instance.projectCode;
    }
    return buildProjectCode({
      projectTypeName:
        instance.templateWorkflowName ||
        instance.name ||
        instance.workflowContainerFormData?.projectType,
      startDate: instance.createdAt,
    });
  };

  const getTranslatedListColumnLabel = useCallback(
    (element: ProjectFormElement): string => {
      const idToken = normalizeFieldToken(element.id);
      const typeToken = normalizeFieldToken(element.type);
      const labelToken = normalizeFieldToken(element.label);

      const keyById: Record<string, string> = {
        name: "projects:projectName",
        projectname: "projects:projectName",
        description: "common:description",
        projectdescription: "common:description",
        clientname: "projects:clientName",
        client: "projects:clientName",
        type: "projects:projectType",
        projecttype: "projects:projectType",
        projectmanager: "projects:projectManager",
        projectresponsible: "projects:projectManager",
        projesorumlusu: "projects:projectManager",
        startdate: "projects:startDate",
        enddate: "projects:endDate",
        budget: "projects:budget",
        projectbudget: "projects:budget",
        offeramount: "projects:offerAmount",
        istutariteklif: "projects:offerAmount",
        invoice: "projects:invoice",
        fatura: "projects:invoice",
        estimateddurationdays: "projects:projectDurationDays",
        projectduration: "projects:projectDurationDays",
        projectdurationhours: "projects:projectDurationDays",
        ulke: "projects:country",
        country: "projects:country",
        il: "projects:city",
        sehir: "projects:city",
        city: "projects:city",
        ilce: "projects:district",
        district: "projects:district",
        mahalle: "projects:neighborhood",
        neighborhood: "projects:neighborhood",
        currency: "projects:currency",
        parabirimi: "projects:currency",
      };

      const keyByType: Record<string, string> = {
        budgetinput: "projects:budget",
        offeramount: "projects:offerAmount",
        invoice: "projects:invoice",
        projectduration: "projects:projectDurationDays",
        clientselect: "projects:clientName",
        projecttypeselect: "projects:projectType",
        countryselect: "projects:country",
        cityselect: "projects:city",
        provinceselect: "projects:city",
        districtselect: "projects:district",
        neighborhoodselect: "projects:neighborhood",
        currencyinput: "projects:currency",
      };

      const keyByLabel: Record<string, string> = {
        projesorumlusu: "projects:projectManager",
        fatura: "projects:invoice",
        istutariteklif: "projects:offerAmount",
        projesuresisaat: "projects:projectDurationDays",
        projeturu: "projects:projectType",
        ulke: "projects:country",
        il: "projects:city",
        sehir: "projects:city",
        ilce: "projects:district",
        mahalle: "projects:neighborhood",
        parabirimi: "projects:currency",
        butce: "projects:budget",
      };

      const translationKey =
        keyById[idToken] || keyByType[typeToken] || keyByLabel[labelToken];
      if (!translationKey) {
        return element.label;
      }

      return t(translationKey as any, { defaultValue: element.label });
    },
    [t]
  );

  const listColumns = useMemo(() => {
    if (!isWorkflowScoped) return [];
    return (workflowFormElements || [])
      .filter((el) =>
        el.showInList &&
        el.id !== "name" &&
        (canViewFinancials ||
          !isFinancialSensitiveProjectField(el) ||
          (canVisitorViewFinancialDetails && el.type === "offer_amount"))
      )
      .sort((a, b) => {
        const aOrder = a.listColumnOrder ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.listColumnOrder ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      })
      .slice(0, 15);
  }, [
    workflowFormElements,
    isWorkflowScoped,
    canViewFinancials,
    canVisitorViewFinancialDetails,
  ]);

  useEffect(() => {
    if (!isWorkflowScoped) return;
    setColumnFilters((prev) => {
      const next: Record<string, string> = {};
      listColumns.forEach((col) => {
        if (prev[col.id]) {
          next[col.id] = prev[col.id];
        }
      });
      return next;
    });
  }, [isWorkflowScoped, listColumns]);

  const parseFormData = (formData: unknown) => {
    if (!formData) return {};
    if (typeof formData === "string") {
      try {
        return JSON.parse(formData);
      } catch {
        return {};
      }
    }
    return formData as Record<string, any>;
  };

  const getElementRawValue = (
    instance: WorkflowInstanceItem,
    element: ProjectFormElement
  ) => {
    const formData = parseFormData(instance.workflowContainerFormData);
    return formData?.[element.id];
  };

  const getElementCurrencyCode = (
    element: ProjectFormElement,
    formData: Record<string, any>
  ): string => {
    const rawElementCurrencies =
      formData.elementCurrencies ?? formData.element_currencies;
    const elementCurrencies =
      rawElementCurrencies &&
      typeof rawElementCurrencies === "object" &&
      !Array.isArray(rawElementCurrencies)
        ? (rawElementCurrencies as Record<string, unknown>)
        : {};

    const storedCurrency =
      elementCurrencies[element.id] ??
      (element.type === "offer_amount"
        ? elementCurrencies.offer_amount ?? elementCurrencies.offerAmount
        : undefined);
    if (storedCurrency) {
      return String(storedCurrency);
    }

    const storedProjectCurrency =
      formData.projectCurrency ??
      formData.currencyCode ??
      formData.currency ??
      formData.para_birimi;
    if (storedProjectCurrency) {
      return String(storedProjectCurrency);
    }

    const configuredCurrency = (element as any).currency;
    if (configuredCurrency) {
      return String(configuredCurrency);
    }

    const legacyCurrencyElement = workflowFormElements.find(
      (formElement) =>
        formElement.type === "currency_input" ||
        formElement.type === "offer_amount"
    );

    return String((legacyCurrencyElement as any)?.currency || "TRY");
  };

  const formatColumnValue = (
    element: ProjectFormElement,
    rawValue: any,
    formData: Record<string, any> = {}
  ): string => {
    if (rawValue === undefined || rawValue === null || rawValue === "") {
      return "-";
    }

    if (element.type === "employee_select") {
      const ids = Array.isArray(rawValue) ? rawValue : [rawValue];
      const names = ids
        .map((id) => employeeById.get(Number(id)))
        .filter(Boolean)
        .map((employee) => `${employee!.firstName} ${employee!.lastName}`);
      return names.length > 0 ? names.join(", ") : "-";
    }

    if (element.type === "date") {
      return formatDate(String(rawValue));
    }

    if (
      element.type === "currency_input" ||
      element.type === "budget_input" ||
      element.type === "offer_amount"
    ) {
      const currencyCode = getElementCurrencyCode(element, formData);
      const numericValue =
        typeof rawValue === "number" ? rawValue : Number(String(rawValue));

      if (Number.isFinite(numericValue)) {
        return formatCurrencyByCode(numericValue, currencyCode, {
          display: "suffix",
        });
      }

      return `${rawValue} ${getCurrencySuffixLabel(currencyCode)}`;
    }

    return String(rawValue);
  };

  const getColumnFilterType = (element: ProjectFormElement) => {
    const selectTypes = new Set([
      "select",
      "project_type_select",
      "country_select",
      "province_select",
      "district_select",
      "neighborhood_select",
      "employee_select",
    ]);
    if (selectTypes.has(element.type)) return "select";
    if (element.type === "date") return "date";
    return "text";
  };

  const getColumnFilterOptions = (element: ProjectFormElement) => {
    const values = new Set<string>();
    instances.forEach((instance) => {
      if (instance.status === "ARCHIVED") return;
      if (isWorkflowScoped && workflowIdFromUrl) {
        const workflowId = parseInt(workflowIdFromUrl, 10);
        if (instance.templateWorkflowId !== workflowId) return;
      }
      const formData = parseFormData(instance.workflowContainerFormData);
      const rawValue = getElementRawValue(instance, element);
      const displayValue = formatColumnValue(element, rawValue, formData);
      if (displayValue && displayValue !== "-") {
        values.add(displayValue);
      }
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  };

  // Filter and sort logic
  const filteredAndSortedInstances = useMemo(() => {
    let filtered = instances.filter(
      (instance) => instance.status !== "ARCHIVED"
    );

    if (isWorkflowScoped) {
      if (workflowIdFromUrl) {
        const workflowId = parseInt(workflowIdFromUrl, 10);
        filtered = filtered.filter(
          (instance) => instance.templateWorkflowId === workflowId
        );
      }

      const normalizedSearch = normalizeSearchText(searchTerm.trim());
      if (normalizedSearch) {
        filtered = filtered.filter((instance) => {
          const managerName = getManagerNameForInstance(instance);
          const projectCode = getProjectCode(instance);
          const matchesSearch =
            normalizeSearchText(getProjectDisplayName(instance)).includes(
              normalizedSearch
            ) ||
            normalizeSearchText(projectCode).includes(normalizedSearch) ||
            normalizeSearchText(instance.name).includes(normalizedSearch) ||
            matchesFormDataSearch(
              instance.workflowContainerFormData,
              normalizedSearch,
              [managerName, projectCode]
            );

          return matchesSearch;
        });
      }

      Object.entries(columnFilters).forEach(([elementId, filterValue]) => {
        if (!filterValue) return;
        const element = listColumns.find((col) => col.id === elementId);
        if (!element) return;

        const filterType = getColumnFilterType(element);
        const normalizedFilter = filterValue.toLowerCase();

        filtered = filtered.filter((instance) => {
          const formData = parseFormData(instance.workflowContainerFormData);
          const rawValue = getElementRawValue(instance, element);
          if (filterType === "date") {
            if (!rawValue) return false;
            const dateValue = new Date(rawValue);
            if (Number.isNaN(dateValue.getTime())) {
              return String(rawValue).startsWith(filterValue);
            }
            const normalizedDate = dateValue.toISOString().split("T")[0];
            return normalizedDate === filterValue;
          }

          const displayValue = formatColumnValue(element, rawValue, formData);
          if (!displayValue || displayValue === "-") return false;

          if (filterType === "select") {
            return displayValue.toLowerCase() === normalizedFilter;
          }
          return displayValue.toLowerCase().includes(normalizedFilter);
        });
      });
    } else {
      // Apply search filter
      const normalizedSearch = normalizeSearchText(searchTerm.trim());
      if (normalizedSearch) {
        filtered = filtered.filter((instance) => {
          const managerName = getManagerNameForInstance(instance);
          const projectCode = getProjectCode(instance);
          const matchesSearch =
            normalizeSearchText(getProjectDisplayName(instance)).includes(
              normalizedSearch
            ) ||
            normalizeSearchText(projectCode).includes(normalizedSearch) ||
            normalizeSearchText(instance.name).includes(normalizedSearch) ||
            normalizeSearchText(instance.templateWorkflowName || "").includes(
              normalizedSearch
            ) ||
            normalizeSearchText(instance.description || "").includes(
              normalizedSearch
            ) ||
            matchesFormDataSearch(
              instance.workflowContainerFormData,
              normalizedSearch,
              [managerName, projectCode]
            );

          return matchesSearch;
        });
      }

      // Apply workflow filter (from dropdown or URL)
      if (workflowIdFromUrl) {
        // Filter by workflow ID from URL
        const workflowId = parseInt(workflowIdFromUrl, 10);
        filtered = filtered.filter(
          (instance) => instance.templateWorkflowId === workflowId
        );
      } else if (workflowFilter !== "all") {
        // Filter by workflow name from dropdown
        filtered = filtered.filter(
          (instance) => instance.templateWorkflowName === workflowFilter
        );
      }

      // Apply client filter
      if (clientFilter !== "all") {
        filtered = filtered.filter((instance) => {
          const clientName = instance.workflowContainerFormData?.clientName;
          return clientName === clientFilter;
        });
      }

      // Apply manager filter
      if (managerFilter !== "all") {
        filtered = filtered.filter((instance) => {
          const managerName = getManagerNameForInstance(instance);
          return managerName === managerFilter;
        });
      }

      // Apply status filter
      if (statusFilter !== "ALL") {
        filtered = filtered.filter(
          (instance) => instance.status === statusFilter
        );
      }

      // Apply start date filter
      if (startDateFilter && filtered.length > 0) {
        filtered = filtered.filter((instance) => {
          const startDate = instance.workflowContainerFormData?.startDate;
          if (!startDate) return false;
          return new Date(startDate) >= new Date(startDateFilter);
        });
      }

      // Apply end date filter
      if (endDateFilter && filtered.length > 0) {
        filtered = filtered.filter((instance) => {
          const endDate = instance.workflowContainerFormData?.endDate;
          if (!endDate) return false;
          return new Date(endDate) <= new Date(endDateFilter);
        });
      }
    }

    // Apply sorting (only if sortOrder is not null)
    if (sortOrder) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        // Check if sorting by a listColumn (dynamic user-defined column)
        const listColumn = listColumns.find((col) => col.id === sortField);

        if (listColumn) {
          // Dynamic listColumn sorting
          aValue = getElementRawValue(a, listColumn);
          bValue = getElementRawValue(b, listColumn);

          // Type-based sorting for listColumns
          const numericTypes = [
            "number",
            "budget_input",
            "currency_input",
            "offer_amount",
            "cost_calculator",
            "progress_bar",
            "warning_threshold",
          ];
          const dateTypes = ["date", "date_range", "project_duration"];

          if (numericTypes.includes(listColumn.type)) {
            // Numeric sorting
            aValue = aValue != null ? parseFloat(String(aValue)) : -Infinity;
            bValue = bValue != null ? parseFloat(String(bValue)) : -Infinity;
          } else if (dateTypes.includes(listColumn.type)) {
            // Date sorting
            aValue = aValue ? new Date(aValue).getTime() : 0;
            bValue = bValue ? new Date(bValue).getTime() : 0;
          } else {
            // Text sorting (includes employee_select, selects, etc.)
            const aFormData = parseFormData(a.workflowContainerFormData);
            const bFormData = parseFormData(b.workflowContainerFormData);
            aValue = formatColumnValue(listColumn, aValue, aFormData).toLowerCase();
            bValue = formatColumnValue(listColumn, bValue, bFormData).toLowerCase();
          }
        } else if (sortField === "clientName") {
          // Sort by client name from form data
          aValue = (a.workflowContainerFormData?.clientName || "").toLowerCase();
          bValue = (b.workflowContainerFormData?.clientName || "").toLowerCase();
        } else if (sortField === "projectManager") {
          // Sort by project manager name from form data
          aValue = getManagerNameForInstance(a).toLowerCase();
          bValue = getManagerNameForInstance(b).toLowerCase();
        } else if (sortField === "projectCode") {
          aValue = getProjectCode(a).toLowerCase();
          bValue = getProjectCode(b).toLowerCase();
        } else if (sortField === "startDate" || sortField === "endDate") {
          // Sort by start/end date from form data
          const aDate = a.workflowContainerFormData?.[sortField];
          const bDate = b.workflowContainerFormData?.[sortField];
          aValue = aDate ? new Date(aDate as string).getTime() : 0;
          bValue = bDate ? new Date(bDate as string).getTime() : 0;
        } else if (sortField === "totalSpentHours") {
          aValue = getProjectHourMetrics(a).spentHours;
          bValue = getProjectHourMetrics(b).spentHours;
        } else if (sortField === "plannedHours") {
          aValue = getProjectHourMetrics(a).plannedHours ?? 0;
          bValue = getProjectHourMetrics(b).plannedHours ?? 0;
        } else if (sortField === "remainingHours") {
          aValue = getProjectHourMetrics(a).remainingHoursRaw ?? 0;
          bValue = getProjectHourMetrics(b).remainingHoursRaw ?? 0;
        } else {
          // Default: Direct instance properties (workflowContainerName, status, startedAt, completedAt)
          aValue = a[sortField as keyof WorkflowInstanceItem];
          bValue = b[sortField as keyof WorkflowInstanceItem];

          if (
            sortField === "startedAt" ||
            sortField === "completedAt" ||
            sortField === "createdAt"
          ) {
            aValue = aValue ? new Date(aValue).getTime() : 0;
            bValue = bValue ? new Date(bValue).getTime() : 0;
          } else if (typeof aValue === "string") {
            aValue = aValue.toLowerCase();
            bValue = bValue?.toLowerCase() || "";
          }
        }

        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [
    instances,
    searchTerm,
    workflowFilter,
    workflowIdFromUrl,
    clientFilter,
    managerFilter,
    statusFilter,
    startDateFilter,
    endDateFilter,
    sortField,
    sortOrder,
    getManagerNameForInstance,
    isWorkflowScoped,
    columnFilters,
    listColumns,
  ]);

  // Pagination logic
  const totalPages = Math.ceil(
    filteredAndSortedInstances.length / itemsPerPage
  );
  const paginatedInstances = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedInstances.slice(
      startIndex,
      startIndex + itemsPerPage
    );
  }, [filteredAndSortedInstances, currentPage, itemsPerPage]);
  const {
    scrollContainerRef,
    setScrollTop,
    virtualizationEnabled,
    startIndex: virtualStartIndex,
    endIndex: virtualEndIndex,
    topSpacerHeight,
    bottomSpacerHeight,
  } = useVirtualizedRows({
    itemCount: paginatedInstances.length,
    rowHeight: 40,
    overscan: 10,
    // This table renders variable-height rows, so virtualization creates
    // visible gaps when the estimated row height drifts from reality.
    enabled: false,
    resetKey: `${currentPage}-${itemsPerPage}-${paginatedInstances.length}`,
  });
  const visiblePaginatedInstances = useMemo(
    () =>
      virtualizationEnabled
        ? paginatedInstances.slice(virtualStartIndex, virtualEndIndex)
        : paginatedInstances,
    [paginatedInstances, virtualEndIndex, virtualStartIndex, virtualizationEnabled]
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    workflowFilter,
    clientFilter,
    managerFilter,
    statusFilter,
    startDateFilter,
    endDateFilter,
    itemsPerPage,
    columnFilters,
    isWorkflowScoped,
  ]);

  useEffect(() => {
    if (isWorkflowScoped) {
      setSortField("workflowContainerName");
      setSortOrder("asc");
    }
  }, [isWorkflowScoped]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Same column clicked - cycle through: asc â†’ desc â†’ null
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortOrder(null); // Return to unsorted state
      } else {
        setSortOrder("asc");
      }
    } else {
      // Different column clicked - start with ascending
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleViewProjectDetail = (instance: WorkflowInstanceItem) => {
    // Navigate to the project instance detail page
    navigate(`/project-instances/${instance.id}`, {
      state: { backTo: "/project-instances" },
    });
  };

  const handleViewProjectFiles = (instance: WorkflowInstanceItem) => {
    if (!canViewProjectFiles) {
      return;
    }
    setSelectedProjectForFiles(instance);
    setShowFilesModal(true);
  };

  const handleEditProject = (instance: WorkflowInstanceItem) => {
    if (
      !isAdmin() &&
      !isManager() &&
      !isProjectManagerAllowedForTemplate(user, instance.templateWorkflowId)
    ) {
      toast.error(t("errors:unauthorized"));
      return;
    }
    if (!instance.templateWorkflowId) {
      toast.error(t("errors:loadFailed"));
      return;
    }
    setSelectedInstanceForEdit(instance);
    setShowEditModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedProject(null);
    setSelectedInstance(null);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedInstanceForEdit(null);
  };

  const renderProjectHourCells = (instance: WorkflowInstanceItem) => {
    const metrics = getProjectHourMetrics(instance);
    const isRemainingHoursNegative =
      metrics.remainingHoursRaw !== null && metrics.remainingHoursRaw < 0;

    return (
      <>
        <td className="px-4 lg:px-5 py-1 whitespace-nowrap text-right min-w-[120px]">
          <div className="text-xs font-semibold text-orange-600 text-right">
            {formatHoursTr(metrics.spentHours)}
          </div>
        </td>
        <td className="px-4 lg:px-5 py-1 whitespace-nowrap text-right min-w-[120px]">
          <div className="text-xs font-semibold text-slate-700 text-right">
            {metrics.plannedHours !== null
              ? formatHoursTr(metrics.plannedHours)
              : "-"}
          </div>
        </td>
        <td className="px-4 lg:px-5 py-1 whitespace-nowrap text-right min-w-[140px]">
          <div
            className={`text-xs font-semibold ${
              isRemainingHoursNegative ? "text-rose-700" : "text-cyan-700"
            } text-right`}
          >
            {metrics.remainingHoursRaw !== null
              ? formatHoursTr(metrics.remainingHoursRaw)
              : "-"}
          </div>
        </td>
      </>
    );
  };

  const handleCreateScopedProject = () => {
    if (!scopedWorkflowId) {
      toast.error(t("errors:loadFailed"));
      return;
    }
    if (!resolvedScopedProjectId) {
      toast.error(t("errors:loadFailed"));
      return;
    }
    setShowCreateModal(true);
  };

  const handleCloseFilesModal = () => {
    setShowFilesModal(false);
    setSelectedProjectForFiles(null);
  };

  // Get unique workflows from instances
  const getUniqueWorkflows = () => {
    const workflows = new Set<string>();
    instances.forEach((instance) => {
      if (instance.templateWorkflowName) {
        workflows.add(instance.templateWorkflowName);
      }
    });
    return Array.from(workflows).sort();
  };

  // Get unique clients from instances
  const getUniqueClients = () => {
    const clients = new Set<string>();
    instances.forEach((instance) => {
      const clientName = instance.workflowContainerFormData?.clientName;
      if (clientName) clients.add(clientName);
    });
    return Array.from(clients).sort();
  };

  // Get unique project managers from instances
  const getUniqueManagers = () => {
    const managers = new Set<string>();
    instances.forEach((instance) => {
      const managerName = getManagerNameForInstance(instance);
      if (managerName) managers.add(managerName);
    });
    return Array.from(managers).sort();
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchTerm("");
    setStatusFilter("ALL");
    setWorkflowFilter("all");
    setClientFilter("all");
    setManagerFilter("all");
    setStartDateFilter("");
    setEndDateFilter("");
    setColumnFilters({});
  };

  const hasDynamicFilters =
    searchTerm !== "" || Object.values(columnFilters).some((value) => value);
  const pageRootRef = useRef<HTMLDivElement | null>(null);
  const [pageViewportHeight, setPageViewportHeight] = useState<number | null>(
    null,
  );
  const syncPageViewportHeight = useCallback(() => {
    const pageRoot = pageRootRef.current;
    if (!pageRoot) {
      return;
    }

    const appScaleRaw = getComputedStyle(document.documentElement)
      .getPropertyValue("--app-ui-scale")
      .trim();
    const parsedScale = Number.parseFloat(appScaleRaw);
    const appScale = Number.isFinite(parsedScale) && parsedScale > 0
      ? parsedScale
      : 1;
    const viewportHeight = window.innerHeight / appScale;
    const pageTop = Math.max(pageRoot.getBoundingClientRect().top, 0);
    const availableHeight = Math.floor(viewportHeight - pageTop);
    setPageViewportHeight(Math.max(320, availableHeight));
  }, []);
  useEffect(() => {
    const frameId = window.requestAnimationFrame(syncPageViewportHeight);
    window.addEventListener("resize", syncPageViewportHeight);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", syncPageViewportHeight);
    };
  }, [syncPageViewportHeight]);

  const tableCardRef = useRef<HTMLDivElement | null>(null);
  const [tableCardMaxHeight, setTableCardMaxHeight] = useState<number | null>(
    null,
  );
  const syncTableCardMaxHeight = useCallback(() => {
    const tableCard = tableCardRef.current;
    if (!tableCard) {
      return;
    }

    const appScaleRaw = getComputedStyle(document.documentElement)
      .getPropertyValue("--app-ui-scale")
      .trim();
    const parsedScale = Number.parseFloat(appScaleRaw);
    const appScale = Number.isFinite(parsedScale) && parsedScale > 0
      ? parsedScale
      : 1;
    const viewportHeight = window.innerHeight / appScale;
    const cardTop = Math.max(tableCard.getBoundingClientRect().top, 0);
    const availableHeight = Math.floor(viewportHeight - cardTop - 8);
    setTableCardMaxHeight(Math.max(220, availableHeight));
  }, []);
  useEffect(() => {
    const frameId = window.requestAnimationFrame(syncTableCardMaxHeight);
    window.addEventListener("resize", syncTableCardMaxHeight);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", syncTableCardMaxHeight);
    };
  }, [
    syncTableCardMaxHeight,
    instances.length,
    isWorkflowScoped,
    canCreateScopedProject,
    isVisitorUser,
  ]);
  const tableCardStyle = tableCardMaxHeight
    ? { maxHeight: `${tableCardMaxHeight}px` }
    : undefined;

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; label: string }> = {
      PLANNING: {
        color: "bg-purple-100 text-purple-800",
        label: t("projects:status.planning"),
      },
      IN_PROGRESS: {
        color: "bg-blue-100 text-blue-800",
        label: t("common:status_IN_PROGRESS"),
      },
      ON_HOLD: {
        color: "bg-yellow-100 text-yellow-800",
        label: t("projects:status.onHold"),
      },
      UNDER_REVIEW: {
        color: "bg-orange-100 text-orange-800",
        label: t("common:status_UNDER_REVIEW"),
      },
      COMPLETED: {
        color: "bg-green-100 text-green-800",
        label: t("common:status_COMPLETED"),
      },
      CANCELLED: {
        color: "bg-red-100 text-red-800",
        label: t("common:status_CANCELLED"),
      },
      ARCHIVED: {
        color: "bg-gray-100 text-gray-800",
        label: t("common:status_ARCHIVED"),
      },
    };
    const config = statusConfig[status] || statusConfig.IN_PROGRESS;
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field || !sortOrder)
      return <ChevronUpIcon className="w-3 h-3 text-gray-300" />;
    return sortOrder === "asc" ? (
      <ChevronUpIcon className="w-3 h-3 text-blue-600" />
    ) : (
      <ChevronDownIcon className="w-3 h-3 text-blue-600" />
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div
      ref={pageRootRef}
      className="bg-gradient-to-br from-slate-50 via-purple-50/30 to-blue-50 pt-14 pb-0 box-border"
      style={{
        height: pageViewportHeight
          ? `${pageViewportHeight}px`
          : "calc(100vh / var(--app-ui-scale, 1))",
      }}
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Empty State */}
        {instances.length === 0 ? (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-12 text-center">
            <FolderIcon className="w-20 h-20 text-purple-300 mx-auto mb-6" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {t("workflows:noStartedInstances")}
            </h3>
            <p className="text-gray-600 mb-8">
              {t("workflows:startWorkflowHint")}
            </p>
            {isVisitorUser ? (
              <p className="text-sm text-slate-600">
                {t(
                  "workflows:noVisitorProjects",
                  "Bu hesap için henüz izin verilmiş proje yok."
                )}
              </p>
            ) : isWorkflowScoped && canCreateScopedProject ? (
              <button
                type="button"
                onClick={handleCreateScopedProject}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all"
              >
                <RocketLaunchIcon className="w-5 h-5 mr-2" />
                {t("projects:createProject")}
              </button>
            ) : (
              <button
                onClick={() => navigate("/project-workflows")}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all"
              >
                <RocketLaunchIcon className="w-5 h-5 mr-2" />
                {t("workflows:goToWorkflows")}
              </button>
            )}
          </div>
        ) : (
          <>
            {isWorkflowScoped && canCreateScopedProject && (
              <div className="mb-3 flex justify-start">
                <button
                  type="button"
                  onClick={handleCreateScopedProject}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-sm transition-all"
                >
                  <RocketLaunchIcon className="w-4 h-4 mr-2" />
                  {t("projects:createProject")}
                </button>
              </div>
            )}
            <div
              ref={tableCardRef}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col"
              style={tableCardStyle}
            >
            {/* Filters and Search */}
            <div className="bg-white/80 backdrop-blur-sm rounded-t-xl shadow-sm border-b border-white/20 overflow-hidden relative z-20 flex-none">
              <div className="bg-slate-50 border-b-2 border-gray-200 px-3 py-2 overflow-x-auto overflow-y-hidden">
                <div className="flex items-center gap-1 min-w-max flex-nowrap">
                  {isVisitorUser ? (
                    <>
                      <div className="relative w-[220px] min-w-[180px]">
                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-purple-400" />
                        <input
                          type="text"
                          placeholder={t("common:searchPlaceholder")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-7 pr-2 py-1.5 text-[11px] bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-400 shadow-sm transition-all font-medium"
                        />
                      </div>

                      {searchTerm !== "" && (
                        <button
                          onClick={clearAllFilters}
                          className="flex items-center gap-1 px-2 py-1.5 text-[11px] border-2 border-red-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-red-500 font-semibold text-red-600 transition-all hover:bg-red-50 shadow-sm"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                          {t("common:clear")}
                        </button>
                      )}
                    </>
                  ) : isWorkflowScoped ? (
                    <>
                      {/* Project Name Search */}
                      <div className="relative w-[160px] min-w-[140px]">
                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-purple-400" />
                        <input
                          type="text"
                          placeholder={t("common:searchPlaceholder")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-7 pr-2 py-1.5 text-[11px] bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-400 shadow-sm transition-all font-medium"
                        />
                      </div>

                      {/* Divider */}
                      <div className="h-6 w-px bg-gradient-to-b from-purple-200 via-pink-300 to-purple-200" />

                      {/* Project Type */}
                      <select
                        value={workflowTypeLabel}
                        disabled
                        className="w-[150px] min-w-[130px] px-2 py-1.5 text-[11px] border-2 border-blue-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-400 shadow-sm font-semibold text-blue-700 transition-all hover:bg-blue-50"
                      >
                        <option value={workflowTypeLabel}>
                          {workflowTypeLabel}
                        </option>
                      </select>

                      {/* Dynamic Column Filters */}
                      {listColumns.map((column) => {
                        const filterType = getColumnFilterType(column);
                        const translatedLabel =
                          getTranslatedListColumnLabel(column);
                        if (filterType === "select") {
                          const options = getColumnFilterOptions(column);
                          return (
                            <select
                              key={column.id}
                              value={columnFilters[column.id] || ""}
                              onChange={(e) =>
                                setColumnFilters((prev) => ({
                                  ...prev,
                                  [column.id]: e.target.value,
                                }))
                              }
                              className="w-[130px] min-w-[120px] px-2 py-1.5 text-[11px] border-2 border-emerald-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 shadow-sm font-semibold text-emerald-700 transition-all hover:bg-emerald-50"
                            >
                              <option value="">{translatedLabel}</option>
                              {options.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          );
                        }

                        if (filterType === "date") {
                          return (
                            <input
                              key={column.id}
                              type="date"
                              value={columnFilters[column.id] || ""}
                              onChange={(e) =>
                                setColumnFilters((prev) => ({
                                  ...prev,
                                  [column.id]: e.target.value,
                                }))
                              }
                              className="w-[130px] min-w-[120px] px-2 py-1.5 text-[11px] border-2 border-emerald-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 shadow-sm font-semibold text-emerald-700 transition-all hover:bg-emerald-50"
                              title={translatedLabel}
                            />
                          );
                        }

                        return (
                          <input
                            key={column.id}
                            type="text"
                            placeholder={translatedLabel}
                            value={columnFilters[column.id] || ""}
                            onChange={(e) =>
                              setColumnFilters((prev) => ({
                                ...prev,
                                [column.id]: e.target.value,
                              }))
                            }
                            className="w-[130px] min-w-[120px] px-2 py-1.5 text-[11px] border-2 border-emerald-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 shadow-sm font-semibold text-emerald-700 transition-all hover:bg-emerald-50"
                          />
                        );
                      })}

                      {/* Clear Filters */}
                      {hasDynamicFilters && (
                        <button
                          onClick={clearAllFilters}
                          className="flex items-center gap-1 px-2 py-1.5 text-[11px] border-2 border-red-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-red-500 font-semibold text-red-600 transition-all hover:bg-red-50 shadow-sm"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                          {t("common:clear")}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      {/* Search */}
                      <div className="relative w-[160px] min-w-[140px]">
                        <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-purple-400" />
                        <input
                          type="text"
                          placeholder={t("common:searchPlaceholder")}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-7 pr-2 py-1.5 text-[11px] bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-400 shadow-sm transition-all font-medium"
                        />
                      </div>

                      {/* Divider */}
                      <div className="h-6 w-px bg-gradient-to-b from-purple-200 via-pink-300 to-purple-200" />

                      {/* Workflow Filter */}
                      <select
                        value={workflowFilter}
                        onChange={(e) => setWorkflowFilter(e.target.value)}
                        className="w-[150px] xl:w-[164px] min-w-[150px] shrink-0 whitespace-nowrap px-2 pr-7 py-1.5 text-[11px] border-2 border-blue-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-400 shadow-sm font-semibold text-blue-700 transition-all hover:bg-blue-50"
                      >
                        <option value="all">{workflowText}</option>
                        {getUniqueWorkflows().map((wf) => (
                          <option key={wf} value={wf}>
                            {wf}
                          </option>
                        ))}
                      </select>

                      {/* Client Filter */}
                      <select
                        value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        className="w-[144px] xl:w-[156px] min-w-[144px] shrink-0 whitespace-nowrap px-2 pr-7 py-1.5 text-[11px] border-2 border-green-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-green-500 focus:border-green-400 shadow-sm font-semibold text-green-700 transition-all hover:bg-green-50"
                      >
                        <option value="all">{t("projects:clientEmployer")}</option>
                        {getUniqueClients().map((client) => (
                          <option key={client} value={client}>
                            {client}
                          </option>
                        ))}
                      </select>

                      {/* Manager Filter */}
                      <select
                        value={managerFilter}
                        onChange={(e) => setManagerFilter(e.target.value)}
                        className="w-[180px] xl:w-[196px] min-w-[180px] shrink-0 whitespace-nowrap px-2 pr-7 py-1.5 text-[11px] border-2 border-amber-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-400 shadow-sm font-semibold text-amber-700 transition-all hover:bg-amber-50"
                      >
                        <option value="all">
                          {t("projects:projectManager")}
                        </option>
                        {getUniqueManagers().map((mgr) => (
                          <option key={mgr} value={mgr}>
                            {mgr}
                          </option>
                        ))}
                      </select>

                      {/* Status Filter */}
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-[132px] xl:w-[144px] min-w-[132px] shrink-0 whitespace-nowrap px-2 pr-7 py-1.5 text-[11px] border-2 border-purple-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-400 shadow-sm font-semibold text-purple-700 transition-all hover:bg-purple-50"
                      >
                        <option value="ALL">{t("common:status")}</option>
                        <option value="PLANNING">
                          {t("projects:status.planning")}
                        </option>
                        <option value="IN_PROGRESS">
                          {t("common:status_IN_PROGRESS")}
                        </option>
                        <option value="ON_HOLD">
                          {t("projects:status.onHold")}
                        </option>
                        <option value="CANCELLED">
                          {t("common:status_CANCELLED")}
                        </option>
                      </select>

                      {/* Start Date */}
                      <input
                        type="date"
                        value={startDateFilter}
                        onChange={(e) => setStartDateFilter(e.target.value)}
                        className="w-[140px] min-w-[130px] px-2 py-1.5 text-[11px] border-2 border-pink-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-400 shadow-sm font-semibold text-pink-700 transition-all hover:bg-pink-50"
                        placeholder={t("common:startDate")}
                        title={t("common:startDate")}
                      />

                      {/* End Date */}
                      <input
                        type="date"
                        value={endDateFilter}
                        onChange={(e) => setEndDateFilter(e.target.value)}
                        className="w-[140px] min-w-[130px] px-2 py-1.5 text-[11px] border-2 border-rose-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-400 shadow-sm font-semibold text-rose-700 transition-all hover:bg-rose-50"
                        placeholder={t("common:endDate")}
                        title={t("common:endDate")}
                      />

                      {/* Clear Filters */}
                      {(searchTerm !== "" ||
                        workflowFilter !== "all" ||
                        clientFilter !== "all" ||
                        managerFilter !== "all" ||
                        statusFilter !== "ALL" ||
                        startDateFilter ||
                        endDateFilter) && (
                        <button
                          onClick={clearAllFilters}
                          className="flex items-center gap-1 px-2 py-1.5 text-[11px] border-2 border-red-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-red-500 font-semibold text-red-600 transition-all hover:bg-red-50 shadow-sm"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                          {t("common:clear")}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <div
              ref={scrollContainerRef}
              onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
              className="overflow-auto min-h-0 flex-1 relative z-10"
            >
              <div className="space-y-2 p-3 md:hidden">
                {virtualizationEnabled && topSpacerHeight > 0 && (
                  <div style={{ height: `${topSpacerHeight}px` }} />
                )}
                {visiblePaginatedInstances.map((instance) => {
                  const plannedHours = getPlannedHours(
                    instance.workflowContainerFormData,
                  );
                  const spentHours = Number(instance.totalSpentHours ?? 0);
                  const hasPlannedHours = plannedHours > 0;
                  const progressPercent = hasPlannedHours
                    ? Math.min((spentHours / plannedHours) * 100, 100)
                    : 0;
                  const canEditProject =
                    !isVisitorUser &&
                    (isAdmin() ||
                      isManager() ||
                      isProjectManagerAllowedForTemplate(
                        user,
                        instance.templateWorkflowId,
                      ));

                  return (
                    <div
                      key={`mobile-project-instance-${instance.id}`}
                      className="rounded-lg border border-blue-100 bg-white px-3 py-2 shadow-sm"
                    >
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 max-w-[72px] shrink-0 truncate rounded-md bg-blue-50 px-2 py-1 text-[11px] font-bold text-blue-700">
                          {getProjectCode(instance) || "-"}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleViewProjectDetail(instance)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <p className="truncate text-[13px] font-bold text-gray-900">
                            {getProjectDisplayName(instance)}
                          </p>
                          <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">
                            {instance.templateWorkflowName || workflowText} -{" "}
                            {instance.workflowContainerFormData?.clientName ||
                              "-"}{" "}
                            - {getManagerNameForInstance(instance) || "-"}
                          </p>
                          <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] font-semibold text-slate-600">
                            <span>{formatDate(instance.createdAt)}</span>
                            <span>
                              {formatDate(
                                instance.workflowContainerFormData?.startDate,
                              )}
                              -
                              {formatDate(
                                instance.workflowContainerFormData?.endDate,
                              )}
                            </span>
                            <span>
                              {spentHours.toFixed(2)} {t("dashboard:hours")}
                              {hasPlannedHours
                                ? ` / ${plannedHours.toFixed(2)}`
                                : ""}
                            </span>
                          </div>
                          {hasPlannedHours && (
                            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200">
                              <div
                                className="h-full bg-gradient-to-r from-orange-400 to-amber-500"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          )}
                        </button>
                        <div className="flex shrink-0 items-center gap-1">
                          {canViewProjectFiles && (
                            <button
                              onClick={() => handleViewProjectFiles(instance)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                              title={t(
                                "projects:workflowInstanceDetail.filesButton",
                              )}
                            >
                              <FolderIcon className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleViewProjectDetail(instance)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-indigo-50 text-indigo-700"
                            title={t("common:details")}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          {canEditProject && (
                            <button
                              onClick={() => handleEditProject(instance)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-orange-50 text-orange-700"
                              title={t("common:edit")}
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {visiblePaginatedInstances.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                    {t("common:noResults")}
                  </div>
                )}
                {virtualizationEnabled && bottomSpacerHeight > 0 && (
                  <div style={{ height: `${bottomSpacerHeight}px` }} />
                )}
              </div>

              <table className="hidden w-full md:table">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-30">
                  <tr>
                    {isWorkflowScoped && !isVisitorUser ? (
                      <>
                        <th
                          onClick={() => handleSort("workflowContainerName")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors sticky top-0 left-0 z-40 bg-gray-50 border-r border-gray-200 min-w-[180px]"
                        >
                          <div className="flex items-center gap-1">
                            {t("projects:projectName")}
                            <SortIcon field="workflowContainerName" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("projectCode")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[120px]"
                        >
                          <div className="flex items-center gap-1">
                            {t("projects:projectCode")}
                            <SortIcon field="projectCode" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("createdAt")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[140px]"
                        >
                          <div className="flex items-center gap-1">
                            {t("dashboard:createdAt")}
                            <SortIcon field="createdAt" />
                          </div>
                        </th>
                        {/* Proje Türü kolonu kaldırıldı - zaten proje türüne göre filtrelenmiş görünüm */}
                        {listColumns.map((column) => (
                          <th
                            key={column.id}
                            onClick={() => handleSort(column.id)}
                            className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50"
                          >
                            <div className="flex items-center gap-1">
                              {getTranslatedListColumnLabel(column)}
                              <SortIcon field={column.id} />
                            </div>
                          </th>
                        ))}
                        <th
                          onClick={() => handleSort("totalSpentHours")}
                          className="px-5 py-2.5 text-center text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[120px]"
                        >
                          <div className="flex items-center justify-center gap-1">
                            {t("dashboard:managerProjects.realizedHours", {
                              defaultValue: "Gerçekleşen Saat",
                            })}
                            <SortIcon field="totalSpentHours" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("plannedHours")}
                          className="px-5 py-2.5 text-center text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[120px]"
                        >
                          <div className="flex items-center justify-center gap-1">
                            {t("dashboard:managerProjects.targetHours", {
                              defaultValue: "Hedef Saat",
                            })}
                            <SortIcon field="plannedHours" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("remainingHours")}
                          className="px-5 py-2.5 text-center text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[140px]"
                        >
                          <div className="flex items-center justify-center gap-1">
                            {t(
                              "dashboard:managerProjects.remainingTargetHours",
                              { defaultValue: "Hedeften Kalan Saat" }
                            )}
                            <SortIcon field="remainingHours" />
                          </div>
                        </th>
                      </>
                    ) : (
                      <>
                        <th
                          onClick={() => handleSort("workflowContainerName")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors sticky top-0 left-0 z-40 bg-gray-50 border-r border-gray-200 min-w-[180px]"
                        >
                          <div className="flex items-center gap-1">
                            {t("projects:projectName")}
                            <SortIcon field="workflowContainerName" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("projectCode")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[120px]"
                        >
                          <div className="flex items-center gap-1">
                            {t("projects:projectCode")}
                            <SortIcon field="projectCode" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("createdAt")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[140px]"
                        >
                          <div className="flex items-center gap-1">
                            {t("dashboard:createdAt")}
                            <SortIcon field="createdAt" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("templateWorkflowName")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50"
                        >
                          <div className="flex items-center gap-1">
                            {workflowText}
                            <SortIcon field="templateWorkflowName" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("clientName")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50"
                        >
                          <div className="flex items-center gap-1">
                            {t("projects:clientEmployer")}
                            <SortIcon field="clientName" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("projectManager")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50"
                        >
                          <div className="flex items-center gap-1">
                            {t("projects:projectManager")}
                            <SortIcon field="projectManager" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("startDate")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50"
                        >
                          <div className="flex items-center gap-1">
                            {t("dashboard:startDate")}
                            <SortIcon field="startDate" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("endDate")}
                          className="px-5 py-2.5 text-left text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50"
                        >
                          <div className="flex items-center gap-1">
                            {t("dashboard:endDate")}
                            <SortIcon field="endDate" />
                          </div>
                        </th>

                        <th
                          onClick={() => handleSort("totalSpentHours")}
                          className="px-5 py-2.5 text-center text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[120px]"
                        >
                          <div className="flex items-center justify-center gap-1">
                            {t("dashboard:managerProjects.realizedHours", {
                              defaultValue: "Gerçekleşen Saat",
                            })}
                            <SortIcon field="totalSpentHours" />
                          </div>
                        </th>
                        <th
                          onClick={() => handleSort("plannedHours")}
                          className="px-5 py-2.5 text-center text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[120px]"
                        >
                          <div className="flex items-center justify-center gap-1">
                            {t("dashboard:managerProjects.targetHours", {
                              defaultValue: "Hedef Saat",
                            })}
                            <SortIcon field="plannedHours" />
                          </div>
                        </th>

                        <th
                          onClick={() => handleSort("remainingHours")}
                          className="px-5 py-2.5 text-center text-[11px] font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors bg-gray-50 min-w-[140px]"
                        >
                          <div className="flex items-center justify-center gap-1">
                            {t(
                              "dashboard:managerProjects.remainingTargetHours",
                              { defaultValue: "Hedeften Kalan Saat" }
                            )}
                            <SortIcon field="remainingHours" />
                          </div>
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {virtualizationEnabled && topSpacerHeight > 0 && (
                    <tr>
                      <td
                        colSpan={
                          isWorkflowScoped && !isVisitorUser
                            ? 6 + listColumns.length
                            : 11
                        }
                        style={{ height: `${topSpacerHeight}px` }}
                        className="p-0 border-0"
                      />
                    </tr>
                  )}
                  {visiblePaginatedInstances.map((instance) => (
                    <tr
                      key={instance.id}
                      className="group hover:bg-gray-50 transition-colors"
                    >
                      {isWorkflowScoped && !isVisitorUser ? (
                        <>
                          <td className="px-4 lg:px-5 py-1 sticky left-0 z-10 bg-white border-r border-gray-200 min-w-[180px] group-hover:bg-gray-50">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xs font-semibold text-gray-900 truncate">
                                {getProjectDisplayName(instance)}
                              </div>
                              <div className="flex items-center gap-2 justify-end shrink-0">
                                {canViewProjectFiles && (
                                  <button
                                    onClick={() => handleViewProjectFiles(instance)}
                                    className="inline-flex items-center justify-center p-1.5 text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                                    title={t("projects:workflowInstanceDetail.filesButton")}
                                    aria-label={t("projects:workflowInstanceDetail.filesButton")}
                                  >
                                    <FolderIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    handleViewProjectDetail(instance)
                                  }
                                  className="inline-flex items-center justify-center p-1.5 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                                  title={t("common:details")}
                                  aria-label={t("common:details")}
                                >
                                  <EyeIcon className="w-3.5 h-3.5" />
                                </button>
                                {!isVisitorUser && (isAdmin() ||
                                  isManager() ||
                                  isProjectManagerAllowedForTemplate(
                                    user,
                                    instance.templateWorkflowId
                                  )) && (
                                  <button
                                    onClick={() => handleEditProject(instance)}
                                    className="inline-flex items-center justify-center p-1.5 text-white bg-orange-500 hover:bg-orange-600 rounded-md transition-colors"
                                    title={t("common:edit")}
                                    aria-label={t("common:edit")}
                                  >
                                    <PencilSquareIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 lg:px-5 py-1">
                            <div className="text-xs text-gray-600 truncate">
                              {getProjectCode(instance) || "-"}
                            </div>
                          </td>
                          <td className="px-4 lg:px-5 py-1 whitespace-nowrap text-xs text-gray-600">
                            {formatDate(instance.createdAt)}
                          </td>
                          {/* Proje Türü hücresi kaldırıldı - zaten proje türüne göre filtrelenmiş görünüm */}
                          {listColumns.map((column) => {
                            const formData = parseFormData(
                              instance.workflowContainerFormData
                            );
                            const rawValue = getElementRawValue(
                              instance,
                              column
                            );
                            const displayValue = formatColumnValue(
                              column,
                              rawValue,
                              formData
                            );
                            return (
                              <td
                                key={`${instance.id}-${column.id}`}
                                className="px-4 lg:px-5 py-1"
                              >
                                <div className="text-xs text-gray-600 truncate">
                                  {displayValue}
                                </div>
                              </td>
                            );
                          })}
                          {renderProjectHourCells(instance)}
                        </>
                      ) : (
                        <>
                          <td className="px-4 lg:px-5 py-1 sticky left-0 z-10 bg-white border-r border-gray-200 min-w-[180px] group-hover:bg-gray-50">
                            <div className="flex items-center justify-between gap-3">
                              <div className="text-xs font-semibold text-gray-900 truncate">
                                {getProjectDisplayName(instance)}
                              </div>
                              <div className="flex items-center gap-2 justify-end shrink-0">
                                {canViewProjectFiles && (
                                  <button
                                    onClick={() => handleViewProjectFiles(instance)}
                                    className="inline-flex items-center justify-center p-1.5 text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                                    title={t("projects:workflowInstanceDetail.filesButton")}
                                    aria-label={t("projects:workflowInstanceDetail.filesButton")}
                                  >
                                    <FolderIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                <button
                                  onClick={() =>
                                    handleViewProjectDetail(instance)
                                  }
                                  className="inline-flex items-center justify-center p-1.5 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                                  title={t("common:details")}
                                  aria-label={t("common:details")}
                                >
                                  <EyeIcon className="w-3.5 h-3.5" />
                                </button>
                                {!isVisitorUser && (isAdmin() ||
                                  isManager() ||
                                  isProjectManagerAllowedForTemplate(
                                    user,
                                    instance.templateWorkflowId
                                  )) && (
                                  <button
                                    onClick={() => handleEditProject(instance)}
                                    className="inline-flex items-center justify-center p-1.5 text-white bg-orange-500 hover:bg-orange-600 rounded-md transition-colors"
                                    title={t("common:edit")}
                                    aria-label={t("common:edit")}
                                  >
                                    <PencilSquareIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 lg:px-5 py-1">
                            <div className="text-xs text-gray-600 truncate">
                              {getProjectCode(instance) || "-"}
                            </div>
                          </td>
                          <td className="px-4 lg:px-5 py-1 whitespace-nowrap text-xs text-gray-600">
                            {formatDate(instance.createdAt)}
                          </td>
                          <td className="px-4 lg:px-5 py-1">
                            <div className="text-xs text-gray-600 truncate">
                              {instance.templateWorkflowName || "-"}
                            </div>
                          </td>
                          <td className="px-4 lg:px-5 py-1">
                            <div className="text-xs text-gray-600 truncate">
                              {instance.workflowContainerFormData?.clientName ||
                                "-"}
                            </div>
                          </td>
                          <td className="px-4 lg:px-5 py-1">
                            <div className="text-xs text-gray-600 truncate">
                              {getManagerNameForInstance(instance) || "-"}
                            </div>
                          </td>
                          <td className="px-4 lg:px-5 py-1 whitespace-nowrap text-xs text-gray-600">
                            {formatDate(
                              instance.workflowContainerFormData?.startDate
                            )}
                          </td>
                          <td className="px-4 lg:px-5 py-1 whitespace-nowrap text-xs text-gray-600">
                            {formatDate(
                              instance.workflowContainerFormData?.endDate
                            )}
                          </td>

                          {renderProjectHourCells(instance)}
                        </>
                      )}
                    </tr>
                  ))}
                  {virtualizationEnabled && bottomSpacerHeight > 0 && (
                    <tr>
                      <td
                        colSpan={
                          isWorkflowScoped && !isVisitorUser
                            ? 6 + listColumns.length
                            : 11
                        }
                        style={{ height: `${bottomSpacerHeight}px` }}
                        className="p-0 border-0"
                      />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {filteredAndSortedInstances.length > 0 && (
              <div className="px-4 py-2.5 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                  {/* Results info */}
                  <div className="text-xs text-gray-600">
                    <span className="font-medium">
                      {filteredAndSortedInstances.length}
                    </span>{" "}
                    {t("common:recordFound")}
                    {(searchTerm || statusFilter !== "ALL") && (
                      <span className="text-gray-400">
                        {" "}
                        ({t("common:total")} {instances.length}{" "}
                        {t("common:recordOf")})
                      </span>
                    )}
                  </div>

                  {/* Page controls */}
                  <div className="flex items-center gap-1.5">
                    {/* Show page navigation only if multiple pages */}
                    {totalPages > 1 && (
                      <>
                        {/* Previous button */}
                        <button
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeftIcon className="w-4 h-4" />
                        </button>

                        {/* Page numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter((page) => {
                              // Show first page, last page, current page, and adjacent pages
                              return (
                                page === 1 ||
                                page === totalPages ||
                                Math.abs(page - currentPage) <= 1
                              );
                            })
                            .map((page, idx, arr) => (
                              <React.Fragment key={page}>
                                {/* Add ellipsis */}
                                {idx > 0 && arr[idx - 1] !== page - 1 && (
                                  <span className="px-2 text-gray-400">
                                    ...
                                  </span>
                                )}
                                <button
                                  onClick={() => setCurrentPage(page)}
                                  className={`px-2.5 py-1.5 text-xs rounded-md ${
                                    currentPage === page
                                      ? "bg-blue-600 text-white font-medium"
                                      : "border border-gray-300 hover:bg-gray-100"
                                  }`}
                                >
                                  {page}
                                </button>
                              </React.Fragment>
                            ))}
                        </div>

                        {/* Next button */}
                        <button
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRightIcon className="w-4 h-4" />
                        </button>
                      </>
                    )}

                    {/* Items per page - always show */}
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className={`${
                        totalPages > 1 ? "ml-2" : ""
                      } h-7 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value={10}>10 {t("common:itemsPerPage")}</option>
                      <option value={20}>20 {t("common:itemsPerPage")}</option>
                      <option value={50}>50 {t("common:itemsPerPage")}</option>
                      <option value={100}>
                        100 {t("common:itemsPerPage")}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            )}
            </div>
          </>
        )}
      </div>

      {showDetailModal && selectedProject && selectedInstance && (
        <ProjectDetailModal
          project={selectedProject}
          isOpen={showDetailModal}
          onClose={handleCloseDetailModal}
          workflowInstanceName={selectedInstance.name}
          workflowInstanceId={selectedInstance.id}
          workflowContainerFormData={selectedInstance.workflowContainerFormData}
        />
      )}

      {showEditModal && selectedInstanceForEdit && (
        <WorkflowStartModal
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          projectId={selectedInstanceForEdit.workflowContainerId}
          workflowId={selectedInstanceForEdit.templateWorkflowId || 0}
          workflowInstanceId={selectedInstanceForEdit.id}
          isEditMode={true}
          initialData={selectedInstanceForEdit.workflowContainerFormData}
          onSuccess={async () => {
            await loadInstances();
            handleCloseEditModal();
          }}
        />
      )}

      {showCreateModal && scopedWorkflowId && (
        <WorkflowStartModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          projectId={resolvedScopedProjectId || 0}
          workflowId={scopedWorkflowId}
          onSuccess={async () => {
            await loadInstances();
            setShowCreateModal(false);
          }}
        />
      )}

      <ProjectFilesModal
        isOpen={canViewProjectFiles && showFilesModal}
        onClose={handleCloseFilesModal}
        workflowInstanceId={selectedProjectForFiles?.id || null}
        templateWorkflowId={selectedProjectForFiles?.templateWorkflowId ?? null}
        projectName={
          selectedProjectForFiles
            ? getProjectDisplayName(selectedProjectForFiles)
            : t("projects:project")
        }
        projectMemberIds={selectedProjectForFiles?.projectMembers ?? []}
        projectManagerId={
          selectedProjectForFiles?.workflowContainerFormData?.projectManager ??
          selectedProjectForFiles?.workflowContainerFormData?.project_manager ??
          null
        }
      />
    </div>
  );
};

